import { Button, Tooltip } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useIsFetching } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

interface Props {
  /** Called when the user clicks refresh. Typically calls `query.refetch()` or `qc.invalidateQueries`. */
  onRefresh: () => void;
  /**
   * Optional query-key filter. When provided, the icon spins while any matching
   * query is in flight. When omitted, spins for any in-flight query on the page.
   */
  matchKey?: QueryKey;
  /** Tooltip override. Default: "Refresh". */
  label?: string;
  /** Timestamp of the most recent successful fetch (e.g. `query.dataUpdatedAt`) for "Updated Xs ago" hint. */
  updatedAt?: number;
}

function relativeTime(ms: number): string {
  if (!ms) return '';
  const diff = Date.now() - ms;
  if (diff < 5_000) return 'just now';
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

/**
 * Refresh icon button that spins while matching queries are in flight.
 * Tooltip shows "Updated 3s ago" derived from `updatedAt`. Tick every 10s so
 * the relative time stays accurate without re-rendering parents.
 */
export function RefreshButton({ onRefresh, matchKey, label = 'Refresh', updatedAt }: Props) {
  const inFlight = useIsFetching(matchKey ? { queryKey: matchKey } : undefined);
  const spinning = inFlight > 0;

  const [, tick] = useState(0);
  useEffect(() => {
    if (!updatedAt) return;
    const id = setInterval(() => tick((n) => n + 1), 10_000);
    return () => clearInterval(id);
  }, [updatedAt]);

  const tooltipText = updatedAt
    ? spinning
      ? 'Refreshing…'
      : `Updated ${relativeTime(updatedAt)}`
    : label;

  return (
    <Tooltip title={tooltipText}>
      <Button
        type="text"
        shape="circle"
        icon={<ReloadOutlined spin={spinning} />}
        aria-label={label}
        onClick={() => {
          if (!spinning) onRefresh();
        }}
      />
    </Tooltip>
  );
}
